import React, { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'react-hot-toast';
import { colaboradorService, veiculoService } from '../../services/api';

const Colaboradores = () => {
  const queryClient = useQueryClient();
  
  // Estados
  const [modalAberto, setModalAberto] = useState(false);
  const [veiculosLista, setVeiculosLista] = useState([]);
  const [modalMode, setModalMode] = useState('create');
  useEffect(() => {
    veiculoService.listar({ limit: 100 }).then(r => {
      const lista = r.data?.data || r.data || [];
      setVeiculosLista(Array.isArray(lista) ? lista : []);
    }).catch(() => {});
  }, []);
  const [colaboradorSelecionado, setColaboradorSelecionado] = useState(null);
  const [filtros, setFiltros] = useState({
    search: '',
    ativo: 'true'
  });
  const [paginacao, setPaginacao] = useState({
    page: 1,
    limit: 10
  });
  const [buscandoCep, setBuscandoCep] = useState(false);

  // Formulário simples
  const [formData, setFormData] = useState({
    nome: '',
    email: '',
    cpf: '',
    dataNascimento: '',
    telefone: '',
    celular: '',
    endereco: {
      cep: '',
      logradouro: '',
      numero: '',
      complemento: '',
      bairro: '',
      cidade: '',
      estado: '',
      pais: 'Brasil'
    },
    cargo: '',
    departamento: '',
    dataAdmissao: '',
    salario: '',
    tipoContrato: 'clt',
    regimeTrabalho: '40h',
    perfil: 'motorista',
    veiculoVinculado: '',
    criarUsuario: false,
    senha: '',
    dadosPessoais: {
      mae: '',
      pai: '',
      sexo: 'masculino',
      estadoCivil: 'solteiro',
      nacionalidade: 'Brasileira',
      naturalidade: '',
      ufNaturalidade: '',
      grauInstrucao: 'medio-completo'
    },
    documentos: {
      ctps: { numero: '', serie: '', uf: '', dataExpedicao: '' },
      ci: { numero: '', orgaoExpedidor: '', dataExpedicao: '' },
      tituloEleitor: { numero: '', zona: '', secao: '' },
      certificadoReservista: '',
      pisPasep: { numero: '', data: '' },
      motorista: false,
      habilitacao: { numero: '', tipo: '', categoria: '', dataVencimento: '' },
      habilitacaoProfissional: '',
      registroProfissional: { numero: '', orgao: '' }
    },
    cbo: '',
    funcao: '',
    dataOpcaoFGTS: '',
    formaPagamento: 'mensal',
    escala: '',
    diasDescanso: '',
    horasMes: 220,
    horasSemana: 44,
    salarioAtual: '',
    dependentesSalarioFamilia: 0,
    dependentesIRRF: 0,
    dependentes: [],
    assinaturas: {
      empregador: { assinatura: '', data: '' },
      empregado: { assinatura: '', dataDesligamento: '' }
    },
    empresa: {
      nome: 'TTAP TRANSPORTES E LOGISTICA LTDA',
      endereco: {
        logradouro: 'Rua Q 204 CONJUNTO 19 LOTE 11 CASA, 06',
        bairro: 'RECANTO DAS EMAS',
        cidade: 'Brasília',
        uf: 'DF',
        codigoMunicipio: '5300108',
        cep: '72610-419'
      },
      cnpj: '15.630.674/0001-47',
      atividadeCNAE: '4923-0/02'
    },
    autenticacao: {
      codigo: '',
      registro: ''
    },
    observacoes: '',
    deviceId: '',
    pontoAtivo: true,
    tipoPonto: 'presencial',
    localizacaoObrigatoria: true
  });

  // Queries
  const { data: colaboradoresData, isLoading, error } = useQuery({
    queryKey: ['colaboradores', filtros, paginacao],
    queryFn: () => colaboradorService.listar({
      ...filtros,
      page: paginacao.page,
      limit: paginacao.limit
    }),
    retry: 1,
    refetchOnWindowFocus: false
  });

  const colaboradores = Array.isArray(colaboradoresData?.data?.data) ? colaboradoresData.data.data : 
                       Array.isArray(colaboradoresData?.data) ? colaboradoresData.data : [];
  
  const paginacaoInfo = colaboradoresData?.data?.pagination || {
    current: 1,
    pages: 1,
    total: colaboradores.length,
    limit: paginacao.limit
  };

  // Debug logs
  console.log('🔍 Debug Colaboradores:');
  console.log('📊 colaboradoresData:', colaboradoresData);
  console.log('📋 colaboradores:', colaboradores);
  console.log('⏳ isLoading:', isLoading);
  console.log('❌ error:', error);
  console.log('🔍 filtros:', filtros);


  // Mutations
  const createMutation = useMutation({
    mutationFn: colaboradorService.criar,
    onSuccess: (res) => {
      queryClient.invalidateQueries(['colaboradores']);
      toast.success(res?.data?.message || 'Colaborador criado com sucesso!');
      fecharModal();
    },
    onError: (error) => {
      console.error('Erro ao criar colaborador:', error);
      const data = error.response?.data;
      const message = data?.message || 'Erro ao criar colaborador';
      
      // Mostrar campos especificos que faltam
      if (data?.details && Array.isArray(data.details) && data.details.length > 0) {
        const erros = data.details.join('<br/>');
        toast.error('<div class=text-left><strong>Campos obrigatorios:</strong><br/>' + erros + '</div>', { duration: 6000 });
      } else if (data?.error) {
        toast.error(data.error + ': ' + message, { duration: 5000 });
      } else {
        toast.error(message);
      }
    }
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }) => colaboradorService.atualizar(id, data),
    onSuccess: (res) => {
      queryClient.invalidateQueries(['colaboradores']);
      toast.success(res?.data?.message || 'Colaborador atualizado com sucesso!');
      fecharModal();
    },
    onError: (error) => {
      console.error('Erro ao atualizar colaborador:', error);
      const message = error.response?.data?.message || 'Erro ao atualizar colaborador';
      toast.error(message);
    }
  });

  const deleteMutation = useMutation({
    mutationFn: colaboradorService.deletar,
    onSuccess: () => {
      queryClient.invalidateQueries(['colaboradores']);
      toast.success('Colaborador excluído com sucesso!');
    },
    onError: (error) => {
      console.error('Erro ao excluir colaborador:', error);
      const message = error.response?.data?.message || 'Erro ao excluir colaborador';
      toast.error(message);
    }
  });

  // Funções
  const abrirModal = (mode = 'create', colaborador = null) => {
    setModalMode(mode);
    setColaboradorSelecionado(colaborador);
    
    if (mode === 'edit' && colaborador) {
      console.log('🔍 Colaborador para edição:', colaborador);
      
      setFormData({
        // Dados básicos
        nome: colaborador.nome || '',
        email: colaborador.email || '',
        cpf: colaborador.cpf || '',
        dataNascimento: colaborador.dataNascimento ? colaborador.dataNascimento.split('T')[0] : '',
        genero: colaborador.genero || 'outro',
        telefone: colaborador.contatos?.telefone || colaborador.telefone || '',
        celular: colaborador.contatos?.celular || colaborador.celular || '',
        
        // Endereço
        endereco: {
          cep: colaborador.endereco?.cep || '',
          logradouro: colaborador.endereco?.logradouro || '',
          numero: colaborador.endereco?.numero || '',
          complemento: colaborador.endereco?.complemento || '',
          bairro: colaborador.endereco?.bairro || '',
          cidade: colaborador.endereco?.cidade || '',
          estado: colaborador.endereco?.estado || '',
          pais: colaborador.endereco?.pais || 'Brasil'
        },
        
        // Dados de trabalho
        cargo: colaborador.dadosTrabalho?.cargo || colaborador.cargo || '',
        departamento: colaborador.dadosTrabalho?.departamento || colaborador.departamento || '',
        dataAdmissao: colaborador.dadosTrabalho?.dataAdmissao ? colaborador.dadosTrabalho.dataAdmissao.split('T')[0] : colaborador.dataAdmissao ? colaborador.dataAdmissao.split('T')[0] : '',
        salario: colaborador.dadosTrabalho?.salario || colaborador.salario || '',
        tipoContrato: colaborador.dadosTrabalho?.tipoContrato || colaborador.tipoContrato || 'clt',
        regimeTrabalho: colaborador.dadosTrabalho?.regimeTrabalho || colaborador.regimeTrabalho || '40h',
        perfil: colaborador.perfil || 'motorista',
        criarUsuario: !!colaborador.temUsuario, // Mostrar seção de usuário quando já tem acesso
        senha: '',
        
        // Dados pessoais
        dadosPessoais: {
          mae: colaborador.dadosPessoais?.mae || '',
          pai: colaborador.dadosPessoais?.pai || '',
          sexo: colaborador.dadosPessoais?.sexo || 'masculino',
          estadoCivil: colaborador.dadosPessoais?.estadoCivil || colaborador.estadoCivil || 'solteiro',
          nacionalidade: colaborador.dadosPessoais?.nacionalidade || 'Brasileira',
          naturalidade: colaborador.dadosPessoais?.naturalidade || '',
          ufNaturalidade: colaborador.dadosPessoais?.ufNaturalidade || '',
          grauInstrucao: colaborador.dadosPessoais?.grauInstrucao || 'medio-completo'
        },
        
        // Documentos
        documentos: {
          ctps: {
            numero: colaborador.documentos?.ctps?.numero || '',
            serie: colaborador.documentos?.ctps?.serie || '',
            uf: colaborador.documentos?.ctps?.uf || '',
            dataExpedicao: colaborador.documentos?.ctps?.dataExpedicao ? colaborador.documentos.ctps.dataExpedicao.split('T')[0] : ''
          },
          ci: {
            numero: colaborador.documentos?.ci?.numero || '',
            orgaoExpedidor: colaborador.documentos?.ci?.orgaoExpedidor || '',
            dataExpedicao: colaborador.documentos?.ci?.dataExpedicao ? colaborador.documentos.ci.dataExpedicao.split('T')[0] : ''
          },
          tituloEleitor: {
            numero: colaborador.documentos?.tituloEleitor?.numero || '',
            zona: colaborador.documentos?.tituloEleitor?.zona || '',
            secao: colaborador.documentos?.tituloEleitor?.secao || ''
          },
          certificadoReservista: colaborador.documentos?.certificadoReservista || '',
          pisPasep: {
            numero: colaborador.documentos?.pisPasep?.numero || '',
            data: colaborador.documentos?.pisPasep?.data ? colaborador.documentos.pisPasep.data.split('T')[0] : ''
          },
          motorista: colaborador.documentos?.motorista || false,
          habilitacao: {
            numero: colaborador.documentos?.habilitacao?.numero || '',
            tipo: colaborador.documentos?.habilitacao?.tipo || '',
            categoria: colaborador.documentos?.habilitacao?.categoria || '',
            dataVencimento: colaborador.documentos?.habilitacao?.dataVencimento ? colaborador.documentos.habilitacao.dataVencimento.split('T')[0] : ''
          },
          habilitacaoProfissional: colaborador.documentos?.habilitacaoProfissional || '',
          registroProfissional: {
            numero: colaborador.documentos?.registroProfissional?.numero || '',
            orgao: colaborador.documentos?.registroProfissional?.orgao || ''
          }
        },
        
        // CBO e função
        cbo: colaborador.cbo || '',
        funcao: colaborador.funcao || '',
        dataOpcaoFGTS: colaborador.dataOpcaoFGTS ? colaborador.dataOpcaoFGTS.split('T')[0] : '',
        formaPagamento: colaborador.formaPagamento || 'mensal',
        escala: colaborador.escala || '',
        diasDescanso: colaborador.diasDescanso || '',
        horasMes: colaborador.horasMes || 220,
        horasSemana: colaborador.horasSemana || 44,
        salarioAtual: colaborador.salarioAtual || '',
        dependentesSalarioFamilia: colaborador.dependentesSalarioFamilia || 0,
        dependentesIRRF: colaborador.dependentesIRRF || 0,
        dependentes: colaborador.dependentes || [],
        
        // Assinaturas
        veiculoVinculado: formData.veiculoVinculado || undefined,
      assinaturas: {
          empregador: {
            assinatura: colaborador.assinaturas?.empregador?.assinatura || '',
            data: colaborador.assinaturas?.empregador?.data ? colaborador.assinaturas.empregador.data.split('T')[0] : ''
          },
          empregado: {
            assinatura: colaborador.assinaturas?.empregado?.assinatura || '',
            dataDesligamento: colaborador.assinaturas?.empregado?.dataDesligamento ? colaborador.assinaturas.empregado.dataDesligamento.split('T')[0] : ''
          }
        },
        
        // Empresa
        empresa: {
          nome: colaborador.empresa?.nome || 'TTAP TRANSPORTES E LOGISTICA LTDA',
          endereco: {
            logradouro: colaborador.empresa?.endereco?.logradouro || 'Rua Q 204 CONJUNTO 19 LOTE 11 CASA, 06',
            bairro: colaborador.empresa?.endereco?.bairro || 'RECANTO DAS EMAS',
            cidade: colaborador.empresa?.endereco?.cidade || 'Brasília',
            uf: colaborador.empresa?.endereco?.uf || 'DF',
            codigoMunicipio: colaborador.empresa?.endereco?.codigoMunicipio || '5300108',
            cep: colaborador.empresa?.endereco?.cep || '72610-419'
          },
          cnpj: colaborador.empresa?.cnpj || '15.630.674/0001-47',
          atividadeCNAE: colaborador.empresa?.atividadeCNAE || '4923-0/02'
        },
        
        // Autenticação
        autenticacao: {
          codigo: colaborador.autenticacao?.codigo || '',
          registro: colaborador.autenticacao?.registro || ''
        },
        
        // Outros
        observacoes: colaborador.observacoes || '',
        deviceId: colaborador.deviceId || '',
        pontoAtivo: colaborador.pontoAtivo || true,
        tipoPonto: colaborador.tipoPonto || 'presencial',
        localizacaoObrigatoria: colaborador.localizacaoObrigatoria || true
      });
    } else {
      resetarFormulario();
    }
    
    setModalAberto(true);
  };

  const fecharModal = () => {
    setModalAberto(false);
    setColaboradorSelecionado(null);
    resetarFormulario();
  };

  const resetarFormulario = () => {
    setFormData({
      nome: '',
      email: '',
      cpf: '',
      dataNascimento: '',
      genero: 'outro',
      telefone: '',
      celular: '',
      endereco: {
        cep: '',
        logradouro: '',
        numero: '',
        complemento: '',
        bairro: '',
        cidade: '',
        estado: '',
        pais: 'Brasil'
      },
      cargo: '',
      departamento: '',
      dataAdmissao: '',
      salario: '',
      tipoContrato: 'clt',
      regimeTrabalho: '40h',
      perfil: 'motorista',
    veiculoVinculado: '',
      criarUsuario: false,
      senha: '',
      dadosPessoais: {
        mae: '',
        pai: '',
        sexo: 'masculino',
        estadoCivil: 'solteiro',
        nacionalidade: 'Brasileira',
        naturalidade: '',
        ufNaturalidade: '',
        grauInstrucao: 'medio-completo'
      },
      documentos: {
        ctps: { numero: '', serie: '', uf: '', dataExpedicao: '' },
        ci: { numero: '', orgaoExpedidor: '', dataExpedicao: '' },
        tituloEleitor: { numero: '', zona: '', secao: '' },
        certificadoReservista: '',
        pisPasep: { numero: '', data: '' },
        motorista: false,
        habilitacao: { numero: '', tipo: '', categoria: '', dataVencimento: '' },
        habilitacaoProfissional: '',
        registroProfissional: { numero: '', orgao: '' }
      },
      cbo: '',
      funcao: '',
      dataOpcaoFGTS: '',
      formaPagamento: 'mensal',
      escala: '',
      diasDescanso: '',
      horasMes: 220,
      horasSemana: 44,
      salarioAtual: '',
      dependentesSalarioFamilia: 0,
      dependentesIRRF: 0,
      dependentes: [],
      assinaturas: {
        empregador: { assinatura: '', data: '' },
        empregado: { assinatura: '', dataDesligamento: '' }
      },
      empresa: {
        nome: 'TTAP TRANSPORTES E LOGISTICA LTDA',
        endereco: {
          logradouro: 'Rua Q 204 CONJUNTO 19 LOTE 11 CASA, 06',
          bairro: 'RECANTO DAS EMAS',
          cidade: 'Brasília',
          uf: 'DF',
          codigoMunicipio: '5300108',
          cep: '72610-419'
        },
        cnpj: '15.630.674/0001-47',
        atividadeCNAE: '4923-0/02'
      },
      autenticacao: {
        codigo: '',
        registro: ''
      },
      observacoes: '',
      deviceId: '',
      pontoAtivo: true,
      tipoPonto: 'presencial',
      localizacaoObrigatoria: true
    });
  };

  // Função para buscar endereço por CEP
  const buscarEnderecoPorCep = async (cep) => {
    // Remove formatação do CEP
    const cepLimpo = cep.replace(/\D/g, '');
    
    // Verifica se tem 8 dígitos
    if (cepLimpo.length !== 8) {
      return;
    }
    
    setBuscandoCep(true);
    
    try {
      const response = await fetch(`https://viacep.com.br/ws/${cepLimpo}/json/`);
      const data = await response.json();
      
      if (data.erro) {
        toast.error('CEP não encontrado');
        setBuscandoCep(false);
        return;
      }
      
      // Preenche os campos de endereço automaticamente
      setFormData(prev => ({
        ...prev,
        endereco: {
          ...prev.endereco,
          logradouro: data.logradouro || prev.endereco?.logradouro || '',
          bairro: data.bairro || prev.endereco?.bairro || '',
          cidade: data.localidade || prev.endereco?.cidade || '',
          estado: data.uf || prev.endereco?.estado || '',
          cep: cep // Mantém o CEP formatado
        }
      }));
      
      toast.success('Endereço encontrado!');
    } catch (error) {
      console.error('Erro ao buscar CEP:', error);
      toast.error('Erro ao buscar endereço. Tente novamente.');
    } finally {
      setBuscandoCep(false);
    }
  };

  // Função para formatar CEP
  const formatarCep = (cep) => {
    const cepLimpo = cep.replace(/\D/g, '');
    if (cepLimpo.length <= 5) {
      return cepLimpo;
    }
    return cepLimpo.replace(/(\d{5})(\d{3})/, '$1-$2');
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    
    // Tratamento especial para CEP
    if (name === 'endereco.cep') {
      const cepFormatado = formatarCep(value);
      const cepLimpo = cepFormatado.replace(/\D/g, '');
      
      setFormData(prev => ({
        ...prev,
        endereco: {
          ...prev.endereco,
          cep: cepFormatado
        }
      }));
      
      // Busca automaticamente quando tiver 8 dígitos
      if (cepLimpo.length === 8) {
        buscarEnderecoPorCep(cepFormatado);
      }
      return;
    }
    
    if (name.includes('.')) {
      const parts = name.split('.');
      
      if (parts.length === 2) {
        const [parent, child] = parts;
        setFormData(prev => ({
          ...prev,
          [parent]: {
            ...prev[parent],
            [child]: type === 'checkbox' ? checked : value
          }
        }));
      } else if (parts.length === 3) {
        const [parent, child, grandchild] = parts;
        setFormData(prev => ({
          ...prev,
          [parent]: {
            ...prev[parent],
            [child]: {
              ...prev[parent][child],
              [grandchild]: type === 'checkbox' ? checked : value
            }
          }
        }));
      }
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: type === 'checkbox' ? checked : value
      }));
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    // Formatar CPF se informado
    const cpfFormatado = formData.cpf.replace(/\D/g, '');
    if (cpfFormatado.length === 11) {
      formData.cpf = cpfFormatado.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, '$1.$2.$3-$4');
    }

    // Preparar dados para envio
    const dadosParaEnvio = {
      ...formData,
      criarUsuario: formData.criarUsuario || false,
      senha: formData.criarUsuario ? formData.senha : undefined, // Só envia senha se criarUsuario estiver ativo
      // Garantir que campos numéricos sejam números
      salario: parseFloat(formData.salario) || 0,
      salarioAtual: parseFloat(formData.salarioAtual) || 0,
      horasMes: parseInt(formData.horasMes) || 220,
      horasSemana: parseInt(formData.horasSemana) || 44,
      dependentesSalarioFamilia: parseInt(formData.dependentesSalarioFamilia) || 0,
      dependentesIRRF: parseInt(formData.dependentesIRRF) || 0,
      // Garantir que campos de data sejam strings
      dataNascimento: formData.dataNascimento,
      dataAdmissao: formData.dataAdmissao,
      dataOpcaoFGTS: formData.dataOpcaoFGTS,
      // Garantir que documentos tenham estrutura correta
      documentos: {
        ...formData.documentos,
        ctps: {
          numero: formData.documentos.ctps.numero || '',
          serie: formData.documentos.ctps.serie || '',
          uf: formData.documentos.ctps.uf || '',
          dataExpedicao: formData.documentos.ctps.dataExpedicao || ''
        },
        ci: {
          numero: formData.documentos.ci.numero || '',
          orgaoExpedidor: formData.documentos.ci.orgaoExpedidor || '',
          dataExpedicao: formData.documentos.ci.dataExpedicao || ''
        },
        tituloEleitor: {
          numero: formData.documentos.tituloEleitor.numero || '',
          zona: formData.documentos.tituloEleitor.zona || '',
          secao: formData.documentos.tituloEleitor.secao || ''
        },
        pisPasep: {
          numero: formData.documentos.pisPasep.numero || '',
          data: formData.documentos.pisPasep.data || ''
        }
      },
      // Garantir que assinaturas tenham estrutura correta
      assinaturas: {
        empregador: {
          assinatura: formData.assinaturas.empregador.assinatura || '',
          data: formData.assinaturas.empregador.data || ''
        },
        empregado: {
          assinatura: formData.assinaturas.empregado.assinatura || '',
          dataDesligamento: formData.assinaturas.empregado.dataDesligamento || ''
        }
      }
    };

    console.log('🔍 Dados sendo enviados:', JSON.stringify(dadosParaEnvio, null, 2));

    if (modalMode === 'create') {
      createMutation.mutate(dadosParaEnvio);
    } else {
      updateMutation.mutate({
        id: colaboradorSelecionado._id,
        data: dadosParaEnvio
      });
    }
  };

  const handleDelete = (id) => {
    if (window.confirm('Tem certeza que deseja excluir este colaborador?')) {
      deleteMutation.mutate(id);
    }
  };

  // Loading e Error states
  if (isLoading) return <div className="flex justify-center items-center h-64">Carregando...</div>;
  if (error) return <div className="text-red-500 text-center">Erro ao carregar colaboradores: {error.message}</div>;

  return (
    <div className="p-6">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900 mb-2">Colaboradores</h1>
        <p className="text-gray-600">Gerencie os colaboradores da empresa</p>
      </div>

      {/* Filtros */}
      <div className="bg-white rounded-lg shadow p-4 mb-6">
        <div className="flex flex-wrap gap-4">
          <div className="flex-1 min-w-64">
            <input
              type="text"
              placeholder="Buscar por nome, matrícula, email..."
              value={filtros.search}
              onChange={(e) => {
                setFiltros(prev => ({ ...prev, search: e.target.value }));
                setPaginacao(prev => ({ ...prev, page: 1 })); // Resetar para primeira página ao buscar
              }}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
          <select
            value={filtros.ativo}
            onChange={(e) => {
              setFiltros(prev => ({ ...prev, ativo: e.target.value === 'true' }));
              setPaginacao(prev => ({ ...prev, page: 1 })); // Resetar para primeira página ao filtrar
            }}
            className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            <option value={true}>Ativos</option>
            <option value={false}>Inativos</option>
          </select>
          <select
            value={paginacao.limit}
            onChange={(e) => {
              setPaginacao(prev => ({ ...prev, limit: parseInt(e.target.value), page: 1 }));
            }}
            className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            <option value={10}>10 por página</option>
            <option value={20}>20 por página</option>
            <option value={50}>50 por página</option>
            <option value={100}>100 por página</option>
          </select>
          <button
            onClick={() => abrirModal('create')}
            className="bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            Novo Colaborador
          </button>
        </div>
      </div>

      {/* Tabela */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Matrícula
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Nome
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Email
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Cargo
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Departamento
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Ações
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {Array.isArray(colaboradores) && colaboradores.length > 0 ? colaboradores.map((colaborador) => (
                <tr key={colaborador._id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                    {colaborador.matricula}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {colaborador.nome}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {colaborador.email}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {colaborador.cargo}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {colaborador.departamento}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                      colaborador.ativo 
                        ? 'bg-green-100 text-green-800' 
                        : 'bg-red-100 text-red-800'
                    }`}>
                      {colaborador.ativo ? 'Ativo' : 'Inativo'}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                    <div className="flex space-x-2">
                      <button
                        onClick={() => abrirModal('edit', colaborador)}
                        className="text-blue-600 hover:text-blue-900"
                      >
                        Editar
                      </button>
                      <button
                        onClick={() => handleDelete(colaborador._id)}
                        className="text-red-600 hover:text-red-900"
                      >
                        Excluir
                      </button>
                    </div>
                  </td>
                </tr>
              )) : (
                <tr>
                  <td colSpan="7" className="px-6 py-4 text-center text-gray-500">
                    {isLoading ? 'Carregando...' : 'Nenhum colaborador encontrado'}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
        
        {/* Paginação */}
        {paginacaoInfo.total > 0 && (
          <div className="bg-white px-4 py-3 flex items-center justify-between border-t border-gray-200 sm:px-6">
            <div className="flex-1 flex justify-between sm:hidden">
              {paginacaoInfo.pages > 1 && (
                <>
                  <button
                    onClick={() => setPaginacao(prev => ({ ...prev, page: Math.max(1, prev.page - 1) }))}
                    disabled={paginacaoInfo.current === 1}
                    className="relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Anterior
                  </button>
                  <button
                    onClick={() => setPaginacao(prev => ({ ...prev, page: Math.min(paginacaoInfo.pages, prev.page + 1) }))}
                    disabled={paginacaoInfo.current === paginacaoInfo.pages}
                    className="ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Próximo
                  </button>
                </>
              )}
            </div>
            <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
              <div>
                <p className="text-sm text-gray-700">
                  Mostrando <span className="font-medium">{(paginacaoInfo.current - 1) * (paginacaoInfo.limit || paginacao.limit) + 1}</span> até{' '}
                  <span className="font-medium">{Math.min(paginacaoInfo.current * (paginacaoInfo.limit || paginacao.limit), paginacaoInfo.total)}</span> de{' '}
                  <span className="font-medium">{paginacaoInfo.total}</span> resultados
                </p>
              </div>
              {paginacaoInfo.pages > 1 && (
                <div>
                  <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px" aria-label="Pagination">
                    <button
                      onClick={() => setPaginacao(prev => ({ ...prev, page: Math.max(1, prev.page - 1) }))}
                      disabled={paginacaoInfo.current === 1}
                      className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <span className="sr-only">Anterior</span>
                      <svg className="h-5 w-5" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                        <path fillRule="evenodd" d="M12.707 5.293a1 1 0 010 1.414L9.414 10l3.293 3.293a1 1 0 01-1.414 1.414l-4-4a1 1 0 010-1.414l4-4a1 1 0 011.414 0z" clipRule="evenodd" />
                      </svg>
                    </button>
                    
                    {/* Números das páginas */}
                    {Array.from({ length: Math.min(5, paginacaoInfo.pages) }, (_, i) => {
                      let pageNum;
                      if (paginacaoInfo.pages <= 5) {
                        pageNum = i + 1;
                      } else if (paginacaoInfo.current <= 3) {
                        pageNum = i + 1;
                      } else if (paginacaoInfo.current >= paginacaoInfo.pages - 2) {
                        pageNum = paginacaoInfo.pages - 4 + i;
                      } else {
                        pageNum = paginacaoInfo.current - 2 + i;
                      }
                      
                      return (
                        <button
                          key={pageNum}
                          onClick={() => setPaginacao(prev => ({ ...prev, page: pageNum }))}
                          className={`relative inline-flex items-center px-4 py-2 border text-sm font-medium ${
                            paginacaoInfo.current === pageNum
                              ? 'z-10 bg-blue-50 border-blue-500 text-blue-600'
                              : 'bg-white border-gray-300 text-gray-500 hover:bg-gray-50'
                          }`}
                        >
                          {pageNum}
                        </button>
                      );
                    })}
                    
                    <button
                      onClick={() => setPaginacao(prev => ({ ...prev, page: Math.min(paginacaoInfo.pages, prev.page + 1) }))}
                      disabled={paginacaoInfo.current === paginacaoInfo.pages}
                      className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <span className="sr-only">Próximo</span>
                      <svg className="h-5 w-5" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                        <path fillRule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z" clipRule="evenodd" />
                      </svg>
                    </button>
                  </nav>
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Modal Completo */}
      {modalAberto && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-4 mx-auto p-5 border w-11/12 max-w-6xl shadow-lg rounded-md bg-white max-h-[95vh] overflow-y-auto">
            <div className="mt-3">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-medium text-gray-900">
                  {modalMode === 'create' ? 'Novo Colaborador' : 'Editar Colaborador'}
                </h3>
                <button
                  onClick={fecharModal}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <span className="sr-only">Fechar</span>
                  <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>

              <form onSubmit={handleSubmit} className="space-y-6">
                {/* Dados Básicos */}
                <div className="bg-gray-50 p-4 rounded-lg">
                  <h4 className="text-md font-medium text-gray-900 mb-3">Dados Básicos</h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Nome Completo
                      </label>
                      <input
                        type="text"
                        name="nome"
                        value={formData.nome}
                        onChange={handleInputChange}
                        
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Email
                      </label>
                      <input
                        type="email"
                        name="email"
                        value={formData.email}
                        onChange={handleInputChange}
                        
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        CPF
                      </label>
                      <input
                        type="text"
                        name="cpf"
                        value={formData.cpf}
                        onChange={handleInputChange}
                        placeholder="000.000.000-00"
                        
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Data de Nascimento
                      </label>
                      <input
                        type="date"
                        name="dataNascimento"
                        value={formData.dataNascimento}
                        onChange={handleInputChange}
                        
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Gênero
                      </label>
                      <select
                        name="genero"
                        value={formData.genero}
                        onChange={handleInputChange}
                        
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      >
                        <option value="masculino">Masculino</option>
                        <option value="feminino">Feminino</option>
                        <option value="outro">Outro</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Telefone
                      </label>
                      <input
                        type="tel"
                        name="telefone"
                        value={formData.telefone}
                        onChange={handleInputChange}
                        
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Celular
                      </label>
                      <input
                        type="tel"
                        name="celular"
                        value={formData.celular}
                        onChange={handleInputChange}
                        
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                    </div>
                  </div>
                </div>

                {/* Endereço */}
                <div className="bg-gray-50 p-4 rounded-lg">
                  <h4 className="text-md font-medium text-gray-900 mb-3">Endereço</h4>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        CEP
                        {buscandoCep && (
                          <span className="ml-2 text-xs text-blue-600 animate-pulse">
                            Buscando...
                          </span>
                        )}
                      </label>
                      <div className="relative">
                        <input
                          type="text"
                          name="endereco.cep"
                          value={formData.endereco?.cep || ''}
                          onChange={handleInputChange}
                          placeholder="00000-000"
                          maxLength={9}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                        />
                        {buscandoCep && (
                          <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
                            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600"></div>
                          </div>
                        )}
                      </div>
                      <p className="mt-1 text-xs text-gray-500">
                        Digite o CEP e o endereço será preenchido automaticamente
                      </p>
                    </div>
                    <div className="md:col-span-2">
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Logradouro
                      </label>
                      <input
                        type="text"
                        name="endereco.logradouro"
                        value={formData.endereco?.logradouro || ''}
                        onChange={handleInputChange}
                        
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Número
                      </label>
                      <input
                        type="text"
                        name="endereco.numero"
                        value={formData.endereco?.numero || ''}
                        onChange={handleInputChange}
                        
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Complemento
                      </label>
                      <input
                        type="text"
                        name="endereco.complemento"
                        value={formData.endereco?.complemento || ''}
                        onChange={handleInputChange}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Bairro
                      </label>
                      <input
                        type="text"
                        name="endereco.bairro"
                        value={formData.endereco?.bairro || ''}
                        onChange={handleInputChange}
                        
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Cidade
                      </label>
                      <input
                        type="text"
                        name="endereco.cidade"
                        value={formData.endereco?.cidade || ''}
                        onChange={handleInputChange}
                        
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Estado
                      </label>
                      <input
                        type="text"
                        name="endereco.estado"
                        value={formData.endereco?.estado || ''}
                        onChange={handleInputChange}
                        
                        maxLength="2"
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                    </div>
                  </div>
                </div>

                {/* Dados Pessoais */}
                <div className="bg-gray-50 p-4 rounded-lg">
                  <h4 className="text-md font-medium text-gray-900 mb-3">Dados Pessoais</h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Nome da Mãe
                      </label>
                      <input
                        type="text"
                        name="dadosPessoais.mae"
                        value={formData.dadosPessoais?.mae || ''}
                        onChange={handleInputChange}
                        
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Nome do Pai
                      </label>
                      <input
                        type="text"
                        name="dadosPessoais.pai"
                        value={formData.dadosPessoais?.pai || ''}
                        onChange={handleInputChange}
                        
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Sexo
                      </label>
                      <select
                        name="dadosPessoais.sexo"
                        value={formData.dadosPessoais?.sexo || ''}
                        onChange={handleInputChange}
                        
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      >
                        <option value="masculino">Masculino</option>
                        <option value="feminino">Feminino</option>
                        <option value="outro">Outro</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Estado Civil
                      </label>
                      <select
                        name="dadosPessoais.estadoCivil"
                        value={formData.dadosPessoais?.estadoCivil || ''}
                        onChange={handleInputChange}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      >
                        <option value="solteiro">Solteiro</option>
                        <option value="casado">Casado</option>
                        <option value="divorciado">Divorciado</option>
                        <option value="viuvo">Viúvo</option>
                        <option value="uniao-estavel">União Estável</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Nacionalidade
                      </label>
                      <input
                        type="text"
                        name="dadosPessoais.nacionalidade"
                        value={formData.dadosPessoais?.nacionalidade || ''}
                        onChange={handleInputChange}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Naturalidade
                      </label>
                      <input
                        type="text"
                        name="dadosPessoais.naturalidade"
                        value={formData.dadosPessoais?.naturalidade || ''}
                        onChange={handleInputChange}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        UF de Naturalidade
                      </label>
                      <input
                        type="text"
                        name="dadosPessoais.ufNaturalidade"
                        value={formData.dadosPessoais?.ufNaturalidade || ''}
                        onChange={handleInputChange}
                        maxLength="2"
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Grau de Instrução
                      </label>
                      <select
                        name="dadosPessoais.grauInstrucao"
                        value={formData.dadosPessoais?.grauInstrucao || ''}
                        onChange={handleInputChange}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      >
                        <option value="fundamental-incompleto">Fundamental Incompleto</option>
                        <option value="fundamental-completo">Fundamental Completo</option>
                        <option value="medio-incompleto">Médio Incompleto</option>
                        <option value="medio-completo">Médio Completo</option>
                        <option value="superior-incompleto">Superior Incompleto</option>
                        <option value="superior-completo">Superior Completo</option>
                        <option value="pos-graduacao">Pós-Graduação</option>
                        <option value="mestrado">Mestrado</option>
                        <option value="doutorado">Doutorado</option>
                      </select>
                    </div>
                  </div>
                </div>

                {/* Dados de Trabalho */}
                <div className="bg-gray-50 p-4 rounded-lg">
                  <h4 className="text-md font-medium text-gray-900 mb-3">Dados de Trabalho</h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Cargo
                      </label>
                      <input
                        type="text"
                        name="cargo"
                        value={formData.cargo}
                        onChange={handleInputChange}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Departamento
                      </label>
                      <input
                        type="text"
                        name="departamento"
                        value={formData.departamento}
                        onChange={handleInputChange}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Data de Admissão
                      </label>
                      <input
                        type="date"
                        name="dataAdmissao"
                        value={formData.dataAdmissao}
                        onChange={handleInputChange}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Salário
                      </label>
                      <input
                        type="number"
                        name="salario"
                        value={formData.salario}
                        onChange={handleInputChange}
                        min="0"
                        step="0.01"
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Tipo de Contrato
                      </label>
                      <select
                        name="tipoContrato"
                        value={formData.tipoContrato}
                        onChange={handleInputChange}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      >
                        <option value="clt">CLT</option>
                        <option value="pj">PJ</option>
                        <option value="autonomo">Autônomo</option>
                        <option value="estagiario">Estagiário</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Regime de Trabalho
                      </label>
                      <select
                        name="regimeTrabalho"
                        value={formData.regimeTrabalho}
                        onChange={handleInputChange}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      >
                        <option value="40h">40h</option>
                        <option value="44h">44h</option>
                        <option value="30h">30h</option>
                        <option value="outro">Outro</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Perfil
                      </label>
                      <select
                        name="perfil"
                        value={formData.perfil}
                        onChange={handleInputChange}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      >
                        <option value="motorista">Motorista</option>
                        <option value="monitor">Monitor</option>
                        <option value="limpeza">Limpeza</option>
                        <option value="auxiliar">Auxiliar</option>
                        <option value="operador">Operador</option>
                        <option value="tecnico">Técnico</option>
                        <option value="administrativo">Administrativo</option>
                        <option value="assistente">Assistente</option>
                        <option value="analista">Analista</option>
                        <option value="coordenador">Coordenador</option>
                        <option value="supervisor">Supervisor</option>
                        <option value="gerente">Gerente</option>
                        <option value="financeiro">Financeiro</option>
                        <option value="diretor">Diretor</option>
                        <option value="administrador">Administrador</option>
                      </select>
                    </div>
                    {/* Veiculo Vinculado */}
                    {formData.perfil === "motorista" && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Veiculo Vinculado
                      </label>
                      <select
                        name="veiculoVinculado"
                        value={formData.veiculoVinculado}
                        onChange={handleInputChange}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      >
                        <option value="">Sem veiculo vinculado</option>
                        {veiculosLista.map(v => (
                          <option key={v._id} value={v._id}>
                            {v.placa} - {v.marca} {v.modelo}
                          </option>
                        ))}
                      </select>
                    </div>
                    )}
                  </div>

                  {/* Criação de Usuário de Acesso */}
                  <div className="mt-6 p-4 rounded-lg border bg-gray-50 border-gray-200">
                    <h5 className="text-sm font-medium text-gray-900 mb-2">
                      Usuário de Acesso ao Sistema
                    </h5>

                    {modalMode === 'edit' && colaboradorSelecionado?.temUsuario ? (
                      <>
                        <div className="mb-4 p-3 bg-green-50 border border-green-200 rounded-md">
                          <p className="text-sm font-medium text-green-800 mb-2 flex items-center gap-2">
                            <span className="inline-block w-2 h-2 rounded-full bg-green-500"></span>
                            Usuário de acesso já criado
                          </p>
                          <dl className="text-sm text-gray-700 space-y-1">
                            <div><span className="font-medium">Login (e-mail):</span> {colaboradorSelecionado.usuario?.email || formData.email}</div>
                            <div><span className="font-medium">Perfil:</span> {colaboradorSelecionado.usuario?.perfil || formData.perfil}</div>
                          </dl>
                          <p className="mt-2 text-xs text-gray-600">
                            O colaborador pode trocar a senha em <strong>Configurações</strong> após fazer login.
                          </p>
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Alterar senha (opcional)
                          </label>
                          <input
                            type="password"
                            name="senha"
                            value={formData.senha}
                            onChange={handleInputChange}
                            placeholder="Deixe em branco para não alterar"
                            minLength={6}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                          />
                        </div>
                      </>
                    ) : (
                      <>
                        <div className="flex items-center justify-between mb-4">
                          <p className="text-xs text-gray-600">
                            Ao ativar, será criado um usuário para o colaborador fazer login no sistema
                          </p>
                          <label className="relative inline-flex items-center cursor-pointer">
                            <input
                              type="checkbox"
                              name="criarUsuario"
                              checked={formData.criarUsuario}
                              onChange={handleInputChange}
                              className="sr-only peer"
                            />
                            <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                          </label>
                        </div>
                        {formData.criarUsuario && (
                          <div className="mt-4 space-y-4">
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-1">
                                Senha para Acesso
                              </label>
                              <input
                                type="password"
                                name="senha"
                                value={formData.senha}
                                onChange={handleInputChange}
                                placeholder="Mínimo 6 caracteres"
                                minLength={6}
                                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                              />
                              <p className="mt-1 text-xs text-gray-500">
                                O colaborador usará o email <strong>{formData.email || '[email do colaborador]'}</strong> e esta senha para fazer login
                              </p>
                            </div>
                            <div className="bg-blue-100 p-3 rounded-md">
                              <p className="text-xs text-blue-800">
                                <strong>Permissões:</strong> O acesso às telas será determinado pelo perfil selecionado acima ({formData.perfil || 'motorista'}). 
                                Cada perfil tem permissões específicas para visualizar e gerenciar diferentes módulos do sistema.
                              </p>
                            </div>
                          </div>
                        )}
                      </>
                    )}
                  </div>
                </div>

                {/* Documentos */}
                <div className="bg-gray-50 p-4 rounded-lg">
                  <h4 className="text-md font-medium text-gray-900 mb-3">Documentos</h4>
                  
                  {/* CTPS */}
                  <div className="mb-4">
                    <h5 className="text-sm font-medium text-gray-700 mb-2">CTPS</h5>
                    <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Número
                        </label>
                        <input
                          type="text"
                          name="documentos.ctps.numero"
                          value={formData.documentos?.ctps?.numero || ''}
                          onChange={handleInputChange}
                          
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Série
                        </label>
                        <input
                          type="text"
                          name="documentos.ctps.serie"
                          value={formData.documentos?.ctps?.serie || ''}
                          onChange={handleInputChange}
                          
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          UF
                        </label>
                        <input
                          type="text"
                          name="documentos.ctps.uf"
                          value={formData.documentos?.ctps?.uf || ''}
                          onChange={handleInputChange}
                          
                          maxLength="2"
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Data de Expedição
                        </label>
                        <input
                          type="date"
                          name="documentos.ctps.dataExpedicao"
                          value={formData.documentos?.ctps?.dataExpedicao || ''}
                          onChange={handleInputChange}
                          
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                        />
                      </div>
                    </div>
                  </div>

                  {/* CI */}
                  <div className="mb-4">
                    <h5 className="text-sm font-medium text-gray-700 mb-2">Carteira de Identidade</h5>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Número
                        </label>
                        <input
                          type="text"
                          name="documentos.ci.numero"
                          value={formData.documentos?.ci?.numero || ''}
                          onChange={handleInputChange}
                          
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Órgão Expedidor
                        </label>
                        <input
                          type="text"
                          name="documentos.ci.orgaoExpedidor"
                          value={formData.documentos?.ci?.orgaoExpedidor || ''}
                          onChange={handleInputChange}
                          
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Data de Expedição
                        </label>
                        <input
                          type="date"
                          name="documentos.ci.dataExpedicao"
                          value={formData.documentos?.ci?.dataExpedicao || ''}
                          onChange={handleInputChange}
                          
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                        />
                      </div>
                    </div>
                  </div>

                  {/* Título de Eleitor */}
                  <div className="mb-4">
                    <h5 className="text-sm font-medium text-gray-700 mb-2">Título de Eleitor</h5>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Número
                        </label>
                        <input
                          type="text"
                          name="documentos.tituloEleitor.numero"
                          value={formData.documentos?.tituloEleitor?.numero || ''}
                          onChange={handleInputChange}
                          
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Zona
                        </label>
                        <input
                          type="text"
                          name="documentos.tituloEleitor.zona"
                          value={formData.documentos?.tituloEleitor?.zona || ''}
                          onChange={handleInputChange}
                          
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Seção
                        </label>
                        <input
                          type="text"
                          name="documentos.tituloEleitor.secao"
                          value={formData.documentos?.tituloEleitor?.secao || ''}
                          onChange={handleInputChange}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                        />
                      </div>
                    </div>
                  </div>

                  {/* Outros Documentos */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Certificado de Reservista
                      </label>
                      <input
                        type="text"
                        name="documentos.certificadoReservista"
                        value={formData.documentos.certificadoReservista}
                        onChange={handleInputChange}
                        
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        PIS/PASEP - Número
                      </label>
                      <input
                        type="text"
                        name="documentos.pisPasep.numero"
                        value={formData.documentos?.pisPasep?.numero || ''}
                        onChange={handleInputChange}
                        
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        PIS/PASEP - Data
                      </label>
                      <input
                        type="date"
                        name="documentos.pisPasep.data"
                        value={formData.documentos?.pisPasep?.data || ''}
                        onChange={handleInputChange}
                        
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                    </div>
                  </div>

                  {/* Motorista */}
                  <div className="mt-4">
                    <div className="flex items-center">
                      <input
                        type="checkbox"
                        name="documentos.motorista"
                        checked={formData.documentos.motorista}
                        onChange={handleInputChange}
                        className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                      />
                      <label className="ml-2 block text-sm text-gray-900">
                        É motorista
                      </label>
                    </div>
                  </div>

                  {/* Habilitação (só aparece se for motorista) */}
                  {(formData.documentos.motorista || formData.perfil === 'motorista') && (
                    <div className="mt-4">
                      <h5 className="text-sm font-medium text-gray-700 mb-2">Habilitação</h5>
                      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Número da CNH
                          </label>
                          <input
                            type="text"
                            name="documentos.habilitacao.numero"
                            value={formData.documentos?.habilitacao?.numero || ''}
                            onChange={handleInputChange}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Tipo
                          </label>
                          <input
                            type="text"
                            name="documentos.habilitacao.tipo"
                            value={formData.documentos?.habilitacao?.tipo || ''}
                            onChange={handleInputChange}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Categoria
                          </label>
                          <input
                            type="text"
                            name="documentos.habilitacao.categoria"
                            value={formData.documentos?.habilitacao?.categoria || ''}
                            onChange={handleInputChange}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Vencimento da CNH
                          </label>
                          <input
                            type="date"
                            name="documentos.habilitacao.dataVencimento"
                            value={formData.documentos?.habilitacao?.dataVencimento || ''}
                            onChange={handleInputChange}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                          />
                        </div>
                      </div>
                      <div className="mt-4">
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Habilitação Profissional
                        </label>
                        <input
                          type="text"
                          name="documentos.habilitacaoProfissional"
                          value={formData.documentos.habilitacaoProfissional}
                          onChange={handleInputChange}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                        />
                      </div>
                    </div>
                  )}
                </div>

                {/* Dados Empregatícios */}
                <div className="bg-gray-50 p-4 rounded-lg">
                  <h4 className="text-md font-medium text-gray-900 mb-3">Dados Empregatícios</h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        CBO
                      </label>
                      <input
                        type="text"
                        name="cbo"
                        value={formData.cbo}
                        onChange={handleInputChange}
                        
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Função
                      </label>
                      <input
                        type="text"
                        name="funcao"
                        value={formData.funcao}
                        onChange={handleInputChange}
                        
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Data de Opção FGTS
                      </label>
                      <input
                        type="date"
                        name="dataOpcaoFGTS"
                        value={formData.dataOpcaoFGTS}
                        onChange={handleInputChange}
                        
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Forma de Pagamento
                      </label>
                      <select
                        name="formaPagamento"
                        value={formData.formaPagamento}
                        onChange={handleInputChange}
                        
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      >
                        <option value="mensal">Mensal</option>
                        <option value="quinzenal">Quinzenal</option>
                        <option value="semanal">Semanal</option>
                        <option value="diario">Diário</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Escala
                      </label>
                      <input
                        type="text"
                        name="escala"
                        value={formData.escala}
                        onChange={handleInputChange}
                        
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Dias de Descanso
                      </label>
                      <input
                        type="text"
                        name="diasDescanso"
                        value={formData.diasDescanso}
                        onChange={handleInputChange}
                        
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Horas por Mês
                      </label>
                      <input
                        type="number"
                        name="horasMes"
                        value={formData.horasMes}
                        onChange={handleInputChange}
                        
                        min="1"
                        max="300"
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Horas por Semana
                      </label>
                      <input
                        type="number"
                        name="horasSemana"
                        value={formData.horasSemana}
                        onChange={handleInputChange}
                        min="1"
                        max="60"
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Salário Atual
                      </label>
                      <input
                        type="number"
                        name="salarioAtual"
                        value={formData.salarioAtual}
                        onChange={handleInputChange}
                        min="0"
                        step="0.01"
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Dependentes Salário Família
                      </label>
                      <input
                        type="number"
                        name="dependentesSalarioFamilia"
                        value={formData.dependentesSalarioFamilia}
                        onChange={handleInputChange}
                        min="0"
                        max="20"
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Dependentes IRRF
                      </label>
                      <input
                        type="number"
                        name="dependentesIRRF"
                        value={formData.dependentesIRRF}
                        onChange={handleInputChange}
                        min="0"
                        max="20"
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                    </div>
                  </div>
                </div>

                {/* Assinaturas */}
                <div className="bg-gray-50 p-4 rounded-lg">
                  <h4 className="text-md font-medium text-gray-900 mb-3">Assinaturas</h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Assinatura do Empregador
                      </label>
                      <input
                        type="text"
                        name="assinaturas.empregador.assinatura"
                        value={formData.assinaturas?.empregador?.assinatura || ''}
                        onChange={handleInputChange}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Data da Assinatura do Empregador
                      </label>
                      <input
                        type="date"
                        name="assinaturas.empregador.data"
                        value={formData.assinaturas?.empregador?.data || ''}
                        onChange={handleInputChange}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Assinatura do Empregado
                      </label>
                      <input
                        type="text"
                        name="assinaturas.empregado.assinatura"
                        value={formData.assinaturas?.empregado?.assinatura || ''}
                        onChange={handleInputChange}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                    </div>
                  </div>
                </div>

                {/* Autenticação */}
                <div className="bg-gray-50 p-4 rounded-lg">
                  <h4 className="text-md font-medium text-gray-900 mb-3">Autenticação</h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Código
                      </label>
                      <input
                        type="text"
                        name="autenticacao.codigo"
                        value={formData.autenticacao?.codigo || ''}
                        onChange={handleInputChange}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Registro
                      </label>
                      <input
                        type="text"
                        name="autenticacao.registro"
                        value={formData.autenticacao?.registro || ''}
                        onChange={handleInputChange}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                    </div>
                  </div>
                </div>

                {/* Observações */}
                <div className="bg-gray-50 p-4 rounded-lg">
                  <h4 className="text-md font-medium text-gray-900 mb-3">Observações</h4>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Observações
                    </label>
                    <textarea
                      name="observacoes"
                      value={formData.observacoes}
                      onChange={handleInputChange}
                      rows="3"
                      maxLength="1000"
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                </div>

                {/* Botões */}
                <div className="flex justify-end space-x-3 pt-4">
                  <button
                    type="button"
                    onClick={fecharModal}
                    className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 border border-gray-300 rounded-md hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-gray-500"
                  >
                    Cancelar
                  </button>
                  <button
                    type="submit"
                    disabled={createMutation.isPending || updateMutation.isPending}
                    className="px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50"
                  >
                    {createMutation.isPending || updateMutation.isPending ? 'Salvando...' : 'Salvar'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Colaboradores;
